/**
 * Fias settings → Company portals (ADR-024 S14).
 *
 * Every Company portal the user continued to — the "connected apps" pattern.
 * Each line restates what that Company sees (username, display name, when
 * the portal was used, and the email address where that consent shared it —
 * disclosure v2 names it) and offers Leave: the user is signed out
 * there and drops off the Company's list (unless the Company blocked them on
 * that portal: the block, and so the row, stays); continuing again asks for
 * consent again. Renders nothing until the user has used a portal, so the
 * settings page does not advertise a feature they have never met — nor for a
 * guest, who cannot hold a portal session and whom the route refuses.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/auth/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/components/ui/use-toast';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export function CompanyPortalsCard() {
  const qc = useQueryClient();
  const { isGuest } = useAuth();
  const [confirmLeave, setConfirmLeave] = useState<{ hostId: string; label: string } | null>(null);
  const portals = useQuery({
    queryKey: queryKeys.companyPortals.mine(),
    queryFn: () => api.getCompanyPortals(),
    enabled: !isGuest,
  });
  const leave = useMutation({
    mutationFn: (hostId: string) => api.leaveCompanyPortal(hostId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.companyPortals.all }),
    onError: (err) =>
      toast({
        title: 'Could not leave the portal',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      }),
  });

  if (isGuest) return null;
  if (portals.isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Company portals</CardTitle>
        </CardHeader>
        <CardContent>
          <p role="alert" className="text-sm text-destructive">
            Could not load your Company portals.
          </p>
        </CardContent>
      </Card>
    );
  }
  const list = portals.data?.portals ?? [];
  if (list.length === 0) return null;
  const shared = portals.data?.sharedFields ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Company portals</CardTitle>
        <CardDescription>
          Company sites where you signed in with your Fias account. Each Company can see your{' '}
          {shared.join(', ')}, and your email address where it says so below — nothing else in your
          account.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {list.map((p) => (
          <div
            key={p.hostId}
            className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium">
                {p.displayLabel} <span className="text-muted-foreground">· {p.companyName}</span>
              </p>
              <p className="truncate text-xs text-muted-foreground">{p.hostname}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Last used {new Date(p.lastSeenAt).toLocaleDateString()}
                {p.sharesEmail && ' · sees your email address'}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={leave.isPending && leave.variables === p.hostId}
              aria-label={`Leave ${p.displayLabel}`}
              onClick={() => setConfirmLeave({ hostId: p.hostId, label: p.displayLabel })}
            >
              Leave portal
            </Button>
          </div>
        ))}
      </CardContent>
      <AlertDialog open={confirmLeave !== null} onOpenChange={(o) => !o && setConfirmLeave(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave {confirmLeave?.label}?</AlertDialogTitle>
            <AlertDialogDescription>
              You are signed out there and removed from the Company&apos;s list of portal users.
              Continuing to it again later asks for your consent again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmLeave) leave.mutate(confirmLeave.hostId);
                setConfirmLeave(null);
              }}
            >
              Leave portal
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
